import React from "react";

/* Technically accurate system diagram (hand-authored SVG):
   - ONE inline full-bore manifold on the return line (water passes straight through)
   - modules seat on the manifold's top ports (no pipe cutting)
   - Smart Core Controller is the brain, wired via a low-voltage DATA bus (not water) */
const MODULES = [
  { cx: 375, name: "Smart Dosing", tag: "V1", v1: true, dot: "#0891B2" },
  { cx: 525, name: "Sensor Expansion", tag: "Future", v1: false, dot: "#94A3B8" },
  { cx: 675, name: "Oxidation Booster", tag: "Future", v1: false, dot: "#94A3B8" },
  { cx: 825, name: "Refill Conditioner", tag: "Future", v1: false, dot: "#94A3B8" },
];

export default function SystemDiagram() {
  const busY = 252;
  const modTop = 300;
  const modH = 96;
  const modBot = modTop + modH;
  const maniY = 452;

  return (
    <div className="overflow-x-auto">
      <svg viewBox="0 0 1200 600" className="w-full min-w-[720px] h-auto" role="img" aria-label="Aqualux Nexus accurate system architecture and inline plumbing">
        <defs>
          <linearGradient id="lightbar" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#38BDF8" />
            <stop offset="0.5" stopColor="#0EA5E9" />
            <stop offset="1" stopColor="#0369A1" />
          </linearGradient>
          <linearGradient id="metal" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#F1F5F9" />
            <stop offset="0.5" stopColor="#CBD5E1" />
            <stop offset="1" stopColor="#94A3B8" />
          </linearGradient>
        </defs>

        {/* ================= CONTROLLER (brain) ================= */}
        <g>
          <rect x="470" y="46" width="260" height="122" rx="20" fill="#FFFFFF" stroke="#CBD5E1" strokeWidth="2" />
          <rect x="488" y="66" width="224" height="11" rx="5.5" fill="url(#lightbar)" />
          {[540, 580, 620, 660].map((x) => (
            <circle key={x} cx={x} cy="122" r="9" fill="none" stroke="#CBD5E1" strokeWidth="2" />
          ))}
          <text x="600" y="126" textAnchor="middle" fill="#94A3B8" fontFamily="JetBrains Mono, monospace" fontSize="9"> </text>
          <text x="600" y="30" textAnchor="middle" fill="#0F172A" fontFamily="Plus Jakarta Sans, sans-serif" fontWeight="800" fontSize="15">SMART CORE CONTROLLER</text>
        </g>

        {/* Concealed cable/data channel from controller down to the data bus */}
        <rect x="592" y="168" width="16" height="84" rx="8" fill="#334155" />
        <text x="620" y="212" fill="#64748B" fontFamily="JetBrains Mono, monospace" fontSize="11">concealed cable</text>

        {/* ================= DATA BUS (low-voltage, not water) ================= */}
        <rect x="300" y={busY} width="640" height="14" rx="7" fill="#1E293B" />
        {MODULES.map((m) => (
          <rect key={`c${m.cx}`} x={m.cx - 6} y={busY + 2} width="12" height="10" rx="2" fill="#FCD34D" />
        ))}
        <text x="300" y={busY - 10} fill="#475569" fontFamily="JetBrains Mono, monospace" fontSize="11" letterSpacing="1">LOW-VOLTAGE DATA BUS</text>

        {/* ================= MODULES seated on manifold ports ================= */}
        {MODULES.map((m) => {
          const x = m.cx - 60;
          return (
            <g key={m.name} opacity={m.v1 ? 1 : 0.92}>
              {/* data lead up to bus */}
              <line x1={m.cx} y1={modTop} x2={m.cx} y2={busY + 14} stroke={m.v1 ? "#0891B2" : "#94A3B8"} strokeWidth="2.5" strokeDasharray={m.v1 ? "0" : "4 4"} />
              {/* module body */}
              <rect
                x={x}
                y={modTop}
                width="120"
                height={modH}
                rx="14"
                fill={m.v1 ? "#FFFFFF" : "#F8FAFC"}
                stroke={m.v1 ? "#0891B2" : "#94A3B8"}
                strokeWidth={m.v1 ? "2.5" : "2"}
                strokeDasharray={m.v1 ? "0" : "6 6"}
              />
              {/* mini screen + status dot */}
              <rect x={m.cx - 34} y={modTop + 18} width="68" height="26" rx="5" fill="#EFF6FF" stroke="#DBEAFE" strokeWidth="1.5" />
              <circle cx={m.cx + 40} cy={modTop + 18} r="4" fill={m.dot} />
              {/* tag pill */}
              <rect x={m.cx - 24} y={modTop + 54} width="48" height="18" rx="9" fill={m.v1 ? "#0891B2" : "#E2E8F0"} />
              <text x={m.cx} y={modTop + 67} textAnchor="middle" fill={m.v1 ? "#FFFFFF" : "#64748B"} fontFamily="JetBrains Mono, monospace" fontSize="10" letterSpacing="0.5">{m.tag}</text>
              {/* name */}
              <text x={m.cx} y={modBot + 26} textAnchor="middle" fill="#0F172A" fontFamily="Plus Jakarta Sans, sans-serif" fontWeight="700" fontSize="13">{m.name}</text>
              {/* water stem into manifold port */}
              <line x1={m.cx} y1={modBot} x2={m.cx} y2={maniY} stroke="#94A3B8" strokeWidth="7" />
              {/* port cap on top of manifold */}
              <rect x={m.cx - 12} y={maniY - 12} width="24" height="16" rx="4" fill={m.cx % 300 === 75 ? "#DC2626" : "#2563EB"} />
            </g>
          );
        })}

        {/* ================= INLINE FULL-BORE MANIFOLD ================= */}
        {/* inlet pipe from pump/filter */}
        <line x1="150" y1={maniY + 23} x2="280" y2={maniY + 23} stroke="#CBD5E1" strokeWidth="14" strokeLinecap="round" />
        <line x1="150" y1={maniY + 23} x2="280" y2={maniY + 23} stroke="#0891B2" strokeWidth="5" className="flow-line" />
        {/* manifold body */}
        <rect x="280" y={maniY} width="640" height="46" rx="23" fill="url(#metal)" stroke="#64748B" strokeWidth="2" />
        {/* full-bore through-flow arrow */}
        <line x1="300" y1={maniY + 23} x2="892" y2={maniY + 23} stroke="#0369A1" strokeWidth="4" className="flow-line" opacity="0.85" />
        <polygon points={`900,${maniY + 23} 886,${maniY + 15} 886,${maniY + 31}`} fill="#0369A1" />
        {/* outlet pipe to pool return */}
        <line x1="920" y1={maniY + 23} x2="1050" y2={maniY + 23} stroke="#CBD5E1" strokeWidth="14" strokeLinecap="round" />
        <line x1="920" y1={maniY + 23} x2="1050" y2={maniY + 23} stroke="#0891B2" strokeWidth="5" className="flow-line" />

        {/* inlet / outlet labels */}
        <text x="150" y={maniY - 6} fill="#0F172A" fontFamily="Plus Jakarta Sans, sans-serif" fontWeight="700" fontSize="13">FROM PUMP &amp; FILTER</text>
        <text x="150" y={maniY + 60} fill="#64748B" fontFamily="JetBrains Mono, monospace" fontSize="12">50 / 40 mm</text>
        <text x="1050" y={maniY - 6} textAnchor="end" fill="#0F172A" fontFamily="Plus Jakarta Sans, sans-serif" fontWeight="700" fontSize="13">TO POOL RETURN</text>
        <text x="1050" y={maniY + 60} textAnchor="end" fill="#64748B" fontFamily="JetBrains Mono, monospace" fontSize="12">50 / 40 mm</text>

        {/* manifold caption */}
        <text x="600" y={maniY + 90} textAnchor="middle" fill="#0369A1" fontFamily="JetBrains Mono, monospace" fontSize="12" letterSpacing="1">
          SINGLE INLINE MANIFOLD · FULL-BORE · MAIN LINE NOT RESTRICTED
        </text>

        {/* ================= LEGEND ================= */}
        <g fontFamily="JetBrains Mono, monospace" fontSize="11" fill="#64748B">
          <rect x="300" y="566" width="16" height="12" rx="3" fill="#FFFFFF" stroke="#0891B2" strokeWidth="2" />
          <text x="324" y="576">V1 · installed now</text>
          <rect x="470" y="566" width="16" height="12" rx="3" fill="#F8FAFC" stroke="#94A3B8" strokeWidth="2" strokeDasharray="4 3" />
          <text x="494" y="576">Future snap-on port · no replumb</text>
          <line x1="760" y1="572" x2="784" y2="572" stroke="#0891B2" strokeWidth="4" />
          <text x="792" y="576">Water flow</text>
          <rect x="880" y="566" width="18" height="10" rx="3" fill="#1E293B" />
          <text x="906" y="576">Data bus</text>
        </g>
      </svg>
    </div>
  );
}
